'use client'
import { LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { FC, useState } from 'react'

import { RenderOn } from '@/utills/DeviceUtils'

const CategoryNames = {
    EU_CSRD: 'EU CSRD',
    LOGISTICS: 'Logistics',
    SPEND_MANAGEMENT: 'Spend management',
    BUSINESS_TRAVEL: 'Business travel',
    CARBON_CREDITS: 'Carbon credits',
    LUNE_UPDATE: 'Lune labs',
}

const CategorySlugs = {
    LUNE_UPDATE: 'lune-updates',
    CARBON_CREDITS: 'carbon-credits',
    LOGISTICS: 'logistics',
    SPEND_MANAGEMENT: 'spend-management',
    BUSINESS_TRAVEL: 'business-travel',
    EU_CSRD: 'eu-csrd',
}

export const Categories: FC<{
    selectedCategory: string
}> = ({ selectedCategory }) => {
    const { palette } = LuneTheme

    const [localSelectedCategory, setLocalSelectedCategory] = useState<string>(selectedCategory)
    const router = useRouter()

    const onCategoryClick = (category: string, newTab?: boolean) => {
        setTimeout(() => {
            const url =
                category in CategorySlugs
                    ? `/resource-hub/${CategorySlugs[category as keyof typeof CategorySlugs]}`
                    : `/resource-hub`

            if (newTab) {
                window.open(url, '_blank')
            } else {
                setLocalSelectedCategory(category)
                router.push(url)
            }
        }, 100)
    }

    const Label = ({ categoryName }: { categoryName: string }) => {
        return (
            <>
                <RenderOn breakPoints={['md', 'lg', 'xl']}>
                    <Text variant={'h4'} className={'blog-category__label'}>
                        {categoryName}
                    </Text>
                </RenderOn>
                <RenderOn breakPoints={['xs', 'sm']}>
                    <Text variant={'h5'} className={'blog-category__label'}>
                        {categoryName}
                    </Text>
                </RenderOn>
            </>
        )
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: { xs: 2, md: 3 },
                pt: 9,
                pb: { xs: 9, md: 15 },
                flexDirection: { xs: 'column', md: 'row' },
                '.blog-category': {
                    '&__link': {
                        textDecoration: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        pb: 1,
                        '&:after': {
                            content: '""',
                            height: '2px',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            width: '100%',
                            background: palette.Grey900,
                            position: 'absolute',
                            transform: 'translateX(-100%)',
                            transition: 'transform 0s ease-in-out',
                        },
                        '.MuiTypography-root': {
                            color: palette.Grey400,
                        },
                        '&--active': {
                            '.MuiTypography-root': {
                                color: palette.Grey900,
                            },
                            '&:after': {
                                width: '100%',
                                transform: 'translateX(0%)',
                                transition: 'transform .3s ease-in-out',
                            },
                        },
                    },
                },
            }}
        >
            <Link
                href={'/resource-hub'}
                onClick={(e) => {
                    e.preventDefault()
                    e.preventDefault()
                    onCategoryClick('ALL', e.ctrlKey || e.metaKey)
                }}
                className={`blog-category__link
                    ${localSelectedCategory === 'ALL' ? 'blog-category__link--active' : ''}
                   `}
            >
                <Label categoryName={'All'} />
            </Link>

            {Object.entries(CategoryNames).map(([key, value]) => (
                <Link
                    className={`
                    blog-category__link
                      ${localSelectedCategory === key ? 'blog-category__link--active' : ''}
                     `}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onCategoryClick(key, e.ctrlKey || e.metaKey)
                    }}
                    key={value}
                    href={`/resource-hub/${CategorySlugs[key as keyof typeof CategorySlugs]}`}
                >
                    <Label categoryName={value} />
                </Link>
            ))}
        </Box>
    )
}
